import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, Warning, Lightning, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { schedulingAPI } from '../../api/api';

export const CrewSuggestions = ({ scheduleId, phase, onAssigned }) => {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    schedulingAPI.suggestions(scheduleId, phase.phase_id)
      .then(({ data }) => setSuggestions(data.suggestions || []))
      .catch(() => toast.error('Could not load crew suggestions'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, phase.phase_id]);

  const assign = async (worker) => {
    setAssigning(worker.id);
    try {
      const { data } = await schedulingAPI.assign(scheduleId, phase.phase_id, worker.id);
      (data.warnings || []).forEach((w) => toast.warning(w));
      if (!data.warnings?.length) toast.success(`${worker.name} assigned & notified`);
      onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-neutral-500" data-testid="crew-loading">
        <CircleNotch size={18} className="animate-spin" /> Ranking your best crew matches...
      </div>
    );
  }
  if (suggestions.length === 0) {
    return <p className="text-sm text-neutral-500 py-3" data-testid="crew-empty">No matching workers found.</p>;
  }

  return (
    <div className="space-y-2 pt-2" data-testid="crew-suggestions">
      {suggestions.map((w) => {
        const assigned = (phase.assigned_worker_ids || []).includes(w.id);
        return (
          <div key={w.id} className="flex items-center gap-3 bg-neutral-50 dark:bg-neutral-800 rounded-xl px-3 py-2.5 border border-neutral-200 dark:border-neutral-700" data-testid={`crew-suggestion-${w.id}`}>
            <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center font-bold text-sm shrink-0">
              {(w.name || '?').charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm text-neutral-900 dark:text-white truncate">{w.name}</span>
                {w.rating > 0 && (
                  <span className="flex items-center gap-0.5 text-xs text-amber-600 font-semibold">
                    <Star size={12} weight="fill" /> {w.rating}
                  </span>
                )}
                {w.fast_responder && (
                  <span className="flex items-center gap-0.5 text-[10px] font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/40 rounded-full px-1.5 py-0.5">
                    <Lightning size={10} weight="fill" /> FAST
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                {w.trade && <span className="text-xs text-neutral-500">{w.trade}</span>}
                {w.matched_skills.map((s) => (
                  <span key={s} className="text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/40 rounded-full px-1.5 py-0.5">{s}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {w.available ? (
                <span className="hidden sm:flex items-center gap-1 text-[11px] font-bold text-emerald-600"><CheckCircle size={13} weight="fill" /> Available</span>
              ) : (
                <span className="hidden sm:flex items-center gap-1 text-[11px] font-bold text-amber-600" title={w.schedule_conflict ? 'Booked on an overlapping phase' : `${w.conflict_days} unavailable day(s)`}>
                  <Warning size={13} weight="fill" /> {w.schedule_conflict ? 'Booked' : `${w.conflict_days}d off`}
                </span>
              )}
              <button
                onClick={() => assign(w)}
                disabled={assigned || assigning === w.id}
                className={`h-8 px-3.5 rounded-full text-xs font-bold transition-all ${
                  assigned
                    ? 'bg-emerald-100 text-emerald-700 cursor-default'
                    : 'bg-[#EA580C] hover:bg-[#D95800] text-white active:scale-95'
                }`}
                data-testid={`assign-worker-${w.id}`}
              >
                {assigned ? 'Assigned' : assigning === w.id ? '...' : 'Assign'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
